import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation, Outlet } from "react-router-dom";
import { Menu, X, Award } from "lucide-react";
import { normalizeImageUrl } from "@/lib/api";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/tentang", label: "Tentang LSP" },
  { to: "/kegiatan", label: "Kegiatan" },
  { to: "/skema", label: "Skema" },
  { to: "/asesor", label: "Asesor" },
  { to: "/tuk", label: "TUK" },
  { to: "/berita", label: "Berita" },
  { to: "/cek-sertifikat", label: "Cek Sertifikat" },
];

function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);
  useEffect(() => setOpen(false), [loc.pathname]);
  return (
    <header data-testid="site-header" className={`sticky top-0 z-50 transition-all ${scrolled ? "bg-emerald-950/95 backdrop-blur-md border-b border-emerald-800/50" : "bg-emerald-950"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-22 lg:h-22">
          <Link to="/" data-testid="brand-link" className="flex items-center gap-3">
            <div className="w-20 h-20 rounded-lg bg-gradient-to-br from-white-400 to-white-600 flex items-center justify-center shadow-lg">
              <img
            src={normalizeImageUrl("Logo LSP.png")}
            alt="LSP Realestat Indonesia"
            className="w-full h-full object-contain"
               />
            </div>
            <div className="leading-tight">
              <div className="text-white font-display font-extrabold text-sm sm:text-base tracking-tight">LSP REALESTAT</div>
              <div className="text-amber-300 text-[14px] sm:text-xs font-semibold tracking-widest uppercase">Indonesia</div>
            </div>
          </Link>
          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map(n => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g,'-')}`}
                className={({isActive}) => `px-3 py-2 text-sm font-medium rounded-lg transition ${isActive ? "text-amber-300" : "text-white/85 hover:text-white hover:bg-emerald-900/50"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/daftar" data-testid="nav-register-btn" className="hidden sm:inline-flex items-center gap-2 px-4 lg:px-5 py-2.5 rounded-lg bg-gradient-to-r from-yellow-400 via-amber-500 to-amber-600 text-emerald-950 font-bold text-sm shadow-lg shadow-amber-500/20 hover:shadow-amber-500/40 hover:scale-[1.02] transition-all">
              Daftar Sekarang
            </Link>
            <button data-testid="mobile-menu-btn" onClick={() => setOpen(!open)} className="lg:hidden p-2 text-white">
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>
      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-emerald-950 border-t border-emerald-800/50">
          <div className="px-4 py-3 space-y-1">
            {NAV.map(n => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"}
                className={({isActive}) => `block px-3 py-2.5 rounded-lg text-sm font-medium ${isActive ? "bg-emerald-900 text-amber-300" : "text-white/85 hover:bg-emerald-900/70"}`}>
                {n.label}
              </NavLink>
            ))}
            <Link to="/daftar" className="block mt-2 px-4 py-3 rounded-lg gold-gradient text-emerald-950 font-bold text-sm text-center">Daftar Sekarang</Link>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-emerald-950 text-white/85 mt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-1">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-20 h-20 rounded-lg White-gradient flex items-center justify-center">
               <img
            src={normalizeImageUrl("Logo LSP.png")}
            alt="LSP Realestat Indonesia"
            className="w-full h-full object-contain"
               />
            </div>
            <div>
              <div className="font-display font-extrabold text-white">LSP REALESTAT</div>
              <div className="text-amber-300 text-xs tracking-widest">INDONESIA</div>
            </div>
          </div>
          <p className="text-sm leading-relaxed">Lembaga Sertifikasi Profesi Realestat Indonesia — sertifikasi kompetensi bidang realestat, perumahan, dan permukiman.</p>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-amber-300 mb-4">Menu</div>
          <ul className="space-y-2 text-sm">
            {NAV.map(n => <li key={n.to}><Link to={n.to} className="hover:text-amber-300">{n.label}</Link></li>)}
            <li><Link to="/daftar" className="hover:text-amber-300">Daftar Sekarang</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-amber-300 mb-4">Kontak</div>
          <ul className="space-y-2 text-sm">
            <li>Rasuna Office Park Unit EO-02, Jakarta Selatan</li>
            <li>+62 896 3804 1510</li>
            <li>info.reidkijakarta@gmail.com</li>
          </ul>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-amber-300 mb-4">Sosial Media</div>
          <ul className="space-y-2 text-sm">
            <li><a href="#" className="hover:text-amber-300">Instagram</a></li>
            <li><a href="#" className="hover:text-amber-300">YouTube</a></li>
            <li><a href="#" className="hover:text-amber-300">LinkedIn</a></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-emerald-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col md:flex-row justify-between gap-3 text-xs text-white/60">
          <div>© 2026 LSP Realestat Indonesia. All Rights Reserved.</div>
          <div className="flex gap-5">
            <a href="#" className="hover:text-amber-300">Kebijakan Privasi</a>
            <a href="#" className="hover:text-amber-300">Syarat & Ketentuan</a>
            <a href="#" className="hover:text-amber-300">Kontak</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Layout() {
  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Header />
      <main className="flex-1"><Outlet /></main>
      <Footer />
    </div>
  );
}
